import { badgeVariants, ToastAction, useToast } from '@folio/components';
import { cn } from '@folio/lib';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  EmailThreadEmailDto,
  EmailThreadLinkedPolicyDto,
  GetEmailThreadOutput,
} from 'korint-sdk';
import {
  emailControllerAttachPolicies,
  emailControllerDetachPolicy,
  emailThreadControllerGetThreadQueryKey,
  emailThreadControllerListThreadsQueryKey,
  generalInboxControllerGetSummaryQueryKey,
} from 'korint-sdk';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PolicyPicker } from './PolicyPicker';
import { linksAfter, type PolicyLinkChange } from './policyLinkChange';
import { LinkedPolicyBadges, usePolicyLabel } from './ThreadIndicators';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

const applyChange = async (emailId: string, { attached, detached }: PolicyLinkChange) => {
  if (attached.length > 0) {
    await emailControllerAttachPolicies(emailId, { policyIds: attached.map(({ id }) => id) });
  }
  for (const { id } of detached) await emailControllerDetachPolicy(emailId, id);
};

function useEmailPolicyLinks(threadId: string, emailId: string) {
  const queryClient = useQueryClient();
  const threadQueryKey = emailThreadControllerGetThreadQueryKey(threadId);
  const mutationKey = ['emailPolicyLink', threadId];

  return useMutation({
    mutationKey,
    mutationFn: (change: PolicyLinkChange) => applyChange(emailId, change),
    onMutate: async change => {
      await queryClient.cancelQueries({ queryKey: threadQueryKey });
      const previous = queryClient.getQueryData<GetEmailThreadOutput>(threadQueryKey);
      if (previous) {
        queryClient.setQueryData<GetEmailThreadOutput>(threadQueryKey, {
          ...previous,
          emails: previous.emails.map(email => {
            if (email.id !== emailId) return email;
            const linkedPolicies = linksAfter(email.linkedPolicies, change);
            return {
              ...email,
              linkedPolicies,
              isToProcess: !email.isArchived && linkedPolicies.length === 0,
            };
          }),
        });
      }
      return { previous };
    },
    onError: (_, __, context) => {
      if (context?.previous) queryClient.setQueryData(threadQueryKey, context.previous);
    },
    onSettled: () => {
      // A refetch landing between two quick changes would erase the second one's optimistic state
      if (queryClient.isMutating({ mutationKey }) === 1) {
        queryClient.invalidateQueries({ queryKey: threadQueryKey });
      }
      queryClient.invalidateQueries({ queryKey: emailThreadControllerListThreadsQueryKey() });
      queryClient.invalidateQueries({ queryKey: generalInboxControllerGetSummaryQueryKey() });
    },
  });
}

const SUGGESTION_COUNT = 2;

export function usePolicyLinks(threadId: string, emailId: string) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { label } = usePolicyLabel();
  const { mutateAsync, isPending } = useEmailPolicyLinks(threadId, emailId);

  const applyWithUndo = (change: PolicyLinkChange, description: string) =>
    mutateAsync(change).then(
      () =>
        toast({
          description,
          action: (
            <ToastAction
              altText={t('generalInbox.archive.undo')}
              onClick={() =>
                mutateAsync({ attached: change.detached, detached: change.attached }).catch(
                  () => {},
                )
              }
            >
              {t('generalInbox.archive.undo')}
            </ToastAction>
          ),
        }),
      () => {},
    );
  return {
    attach: (policy: EmailThreadLinkedPolicyDto) =>
      applyWithUndo(
        { attached: [policy], detached: [] },
        t('generalInbox.policyLinks.attached', { policy: label(policy) }),
      ),
    detach: (policy: EmailThreadLinkedPolicyDto) =>
      applyWithUndo(
        { attached: [], detached: [policy] },
        t('generalInbox.policyLinks.detached', { policy: label(policy) }),
      ),
    isPending,
  };
}

export function EmailPolicyLinks({
  threadId,
  email,
  threadPolicies,
  participantAddresses,
}: {
  threadId: string;
  email: Pick<EmailThreadEmailDto, 'id' | 'linkedPolicies'>;
  threadPolicies: EmailThreadLinkedPolicyDto[];
  participantAddresses: string[];
}) {
  const { t } = useTranslation();
  const { label } = usePolicyLabel();
  const { attach, detach, isPending } = usePolicyLinks(threadId, email.id);
  const suggestions =
    email.linkedPolicies.length === 0 ? threadPolicies.slice(0, SUGGESTION_COUNT) : [];

  return (
    <Group wrap gap={0} className="gap-1.5 text-xs" data-testid="general-inbox-email-policies">
      <Text inline tone="muted">
        {t('generalInbox.policyLinks.label')}
      </Text>
      <LinkedPolicyBadges policies={email.linkedPolicies} onDetach={detach} disabled={isPending} />
      {suggestions.map(policy => (
        <button
          key={policy.id}
          type="button"
          className={cn(
            badgeVariants({ variant: 'outline' }),
            'relative z-10 min-w-0 max-w-full gap-1 border-dashed font-medium text-muted-foreground hover:border-primary/40 hover:text-primary disabled:opacity-50',
          )}
          disabled={isPending}
          onClick={() => attach(policy)}
          data-testid="general-inbox-attach-suggestion"
        >
          <Text inline truncate>
            {t('generalInbox.policyLinks.suggested', { policy: label(policy) })}
          </Text>
          <Check className="size-3 shrink-0" aria-hidden />
        </button>
      ))}
      <PolicyPicker
        linkedPolicies={email.linkedPolicies}
        suggestedPolicies={threadPolicies}
        participantAddresses={participantAddresses}
        onAdd={attach}
        disabled={isPending}
      />
    </Group>
  );
}
